import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/Page";

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const platformDot = {
  instagram: "bg-pink-100 text-pink-700 border-pink-200",
  facebook: "bg-blue-100 text-blue-700 border-blue-200",
  tiktok: "bg-gray-900 text-white border-gray-900",
  "youtube-shorts": "bg-red-100 text-red-700 border-red-200",
  linkedin: "bg-sky-100 text-sky-700 border-sky-200",
  default: "bg-purple-100 text-[#7C3AED] border-purple-200",
};

const Calendar = () => {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(new Date());
  const [draggingId, setDraggingId] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);
  const [addOpen, setAddOpen] = useState(null); // day number when adding
  const [addTitle, setAddTitle] = useState("");
  const [addPlatform, setAddPlatform] = useState("instagram");

  const load = () => api.get("/calendar").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const itemsForDay = (d) => items.filter((it) => {
    if (!it.date) return false;
    const dt = new Date(it.date);
    return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === d;
  });

  const onDrop = async (targetDay) => {
    if (!draggingId) return;
    const item = items.find((i) => i.id === draggingId);
    if (!item) { setDraggingId(null); setHoverDay(null); return; }
    const original = new Date(item.date);
    const newDate = new Date(year, month, targetDay, original.getHours() || 12, original.getMinutes() || 0);
    if (item.date && new Date(item.date).getTime() === newDate.getTime()) {
      setDraggingId(null); setHoverDay(null); return;
    }
    setItems((prev) => prev.map((i) => i.id === draggingId ? { ...i, date: newDate.toISOString() } : i));
    setDraggingId(null); setHoverDay(null);
    try {
      await api.patch(`/calendar/${draggingId}`, { date: newDate.toISOString() });
      toast.success(`Moved to ${monthNames[month]} ${targetDay}`);
    } catch {
      toast.error("Move failed");
      load();
    }
  };

  const removeItem = async (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    try {
      await api.delete(`/calendar/${id}`);
    } catch {
      toast.error("Delete failed");
      load();
    }
  };

  const submitQuickAdd = async () => {
    if (!addTitle.trim() || !addOpen) return;
    const iso = new Date(year, month, addOpen, 12, 0).toISOString();
    try {
      const { data } = await api.post("/calendar", { date: iso, title: addTitle, platform: addPlatform, kind: "manual" });
      setItems((prev) => [...prev, data]);
      setAddOpen(null); setAddTitle(""); setAddPlatform("instagram");
    } catch {
      toast.error("Add failed");
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Content Calendar" subtitle="Drag any post to reschedule. Click + to add." testId="calendar-header" />
      <div className="op-card p-5">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => setDate(new Date(year, month - 1, 1))} className="op-btn-secondary py-1.5 px-3" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <h2 className="text-xl font-bold text-gray-900">{monthNames[month]} {year}</h2>
          <button onClick={() => setDate(new Date(year, month + 1, 1))} className="op-btn-secondary py-1.5 px-3" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-500 mb-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            const isToday = d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;
            const dayItems = d ? itemsForDay(d) : [];
            const isHover = hoverDay === d && draggingId;
            return (
              <div key={i}
                onDragOver={d ? (e) => { e.preventDefault(); setHoverDay(d); } : undefined}
                onDragLeave={() => setHoverDay((h) => (h === d ? null : h))}
                onDrop={d ? () => onDrop(d) : undefined}
                className={`min-h-[110px] p-2 rounded-lg border transition ${
                  isToday ? "bg-purple-50 border-[#7C3AED]" :
                  isHover ? "bg-purple-100 border-[#7C3AED] border-dashed" :
                  d ? "border-gray-100 hover:bg-gray-50" : "bg-transparent border-transparent"
                }`}
                data-testid={d ? `cal-day-${d}` : undefined}>
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-sm font-medium ${isToday ? "w-6 h-6 rounded-full bg-[#7C3AED] text-white flex items-center justify-center" : "text-gray-700"}`}>{d}</span>
                      <button onClick={() => { setAddOpen(d); setAddTitle(""); }} className="text-gray-400 hover:text-[#7C3AED]" data-testid={`cal-add-${d}`} title="Add"><Plus size={14} /></button>
                    </div>
                    <div className="space-y-1">
                      {dayItems.map((it) => {
                        const cls = platformDot[it.platform] || platformDot.default;
                        return (
                          <div key={it.id}
                            draggable
                            onDragStart={() => setDraggingId(it.id)}
                            onDragEnd={() => { setDraggingId(null); setHoverDay(null); }}
                            className={`group text-[11px] px-1.5 py-1 rounded border ${cls} cursor-grab active:cursor-grabbing flex items-center gap-1`}
                            data-testid={`cal-item-${it.id}`}
                            title={it.title}>
                            <span className="truncate flex-1">{it.title}</span>
                            <button onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                              className="opacity-0 group-hover:opacity-100 hover:text-red-600 transition" data-testid={`cal-delete-${it.id}`}>
                              <Trash2 size={11} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick-add modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setAddOpen(null)} data-testid="cal-add-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add to {monthNames[month]} {addOpen}</h3>
              <button onClick={() => setAddOpen(null)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <input value={addTitle} onChange={(e) => setAddTitle(e.target.value)} placeholder="What's the post about?" className="op-input mb-3" autoFocus data-testid="cal-add-title" />
            <div className="flex flex-wrap gap-2 mb-4">
              {["instagram", "facebook", "tiktok", "youtube-shorts", "linkedin"].map((p) => (
                <button key={p} onClick={() => setAddPlatform(p)} className={addPlatform === p ? "op-pill-active" : "op-pill-inactive"} data-testid={`cal-add-platform-${p}`}>{p}</button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAddOpen(null)} className="op-btn-secondary" data-testid="cal-add-cancel">Cancel</button>
              <button onClick={submitQuickAdd} disabled={!addTitle.trim()} className="op-btn-primary" data-testid="cal-add-submit">Add</button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default Calendar;
